import React, { useContext, useEffect, useRef, useState } from "react";
import SessionContext from "../../context/session";
import moment from "moment";
import MessageContent from "./MessageContent";
import "./chatbox.css";
import { apiUrl } from "../../config/runtime";
import { normalizeChatMessages } from "../../types/contracts";

const ThumbsUpIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
  </svg>
);

const ThumbsDownIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h3a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-3" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const MoreIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="5" cy="12" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="19" cy="12" r="2" />
  </svg>
);

const UndoIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 14L4 9l5-5" />
    <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11" />
  </svg>
);

/* 📍 用户气泡 Hover 撤回、复制与时间工具栏 */
const UserActionBar: React.FC<{
  text: string;
  messageId: string;
  time?: string;
  onRevoke?: (messageId: string) => void;
}> = ({ text, messageId, time, onRevoke }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    try {
      navigator.clipboard?.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="user-action-bar">
      {time && <span className="user-action-time">{time}</span>}
      {onRevoke && (
        <button
          type="button"
          className="user-action-btn"
          onClick={() => onRevoke(messageId)}
          title="撤回"
        >
          <UndoIcon />
        </button>
      )}
      <button type="button" className="user-action-btn" onClick={handleCopy} title="复制">
        <CopyIcon />
        {copied && <span className="user-action-tip">已复制</span>}
      </button>
    </div>
  );
};

/* 📍 AI 气泡底部工具栏 */
const AIActionBar: React.FC<{ text: string; time?: string }> = ({ text, time }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    try {
      navigator.clipboard?.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="ai-action-bar">
      <button type="button" className="ai-action-btn" title="赞">
        <ThumbsUpIcon />
      </button>
      <button type="button" className="ai-action-btn" title="踩">
        <ThumbsDownIcon />
      </button>
      <button type="button" className="ai-action-btn" title="重新生成">
        <RefreshIcon />
      </button>
      <button type="button" className="ai-action-btn" onClick={handleCopy} title="复制内容">
        <CopyIcon />
        {copied && <span className="ai-action-tip">已复制</span>}
      </button>
      <button type="button" className="ai-action-btn" title="更多">
        <MoreIcon />
      </button>
      {time && <span className="ai-action-time">{time}</span>}
    </div>
  );
};

const Chatbox = () => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const { messages, setMessages, token, setDraftInput, toolStatus } = useContext(SessionContext);
  const [isActivelyStreaming, setIsActivelyStreaming] = useState(false);
  const streamTimeoutRef = useRef<any>(null);

  const handleRevoke = async (messageId: string) => {
    if (!token) return;
    try {
      const res = await fetch(apiUrl("/revoke"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, message_id: messageId }),
      });
      if (!res.ok) throw new Error("撤回失败");
      const data = await res.json();
      if (data.status === "ok") {
        setMessages(normalizeChatMessages(data.remaining_messages));
        setDraftInput(data.revoked_text);
      }
    } catch (err) {
      console.error("撤回请求发生错误:", err);
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });

    if (messages && messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.role !== "user") {
        setIsActivelyStreaming(true);

        if (streamTimeoutRef.current) {
          clearTimeout(streamTimeoutRef.current);
        }

        streamTimeoutRef.current = setTimeout(() => {
          setIsActivelyStreaming(false);
        }, 1200);
      } else {
        setIsActivelyStreaming(false);
      }
    }
  }, [messages]);

  const lastMsg = messages && messages.length > 0 ? messages[messages.length - 1] : null;
  const isPendingAI = lastMsg && lastMsg.role === "user";

  return (
    <div className="chat-thread">
      {messages?.map((message, index) => {
        const isHuman = message.role === "user";
        const text = message.content;
        const formattedTime = moment(message.created_at).format("HH:mm");
        const isLastMsg = index === messages.length - 1;

        return isHuman ? (
          <div className="msg-row msg-row--user" key={message.id}>
            <div className="msg-main">
              <div className="msg-bubble msg-bubble--user">
                <MessageContent text={text} artifacts={message.artifacts} />
              </div>
              {/* 📍 用户气泡鼠标悬浮撤回与复制按钮 */}
              <UserActionBar text={text} messageId={message.id} time={formattedTime} onRevoke={handleRevoke} />
            </div>
          </div>
        ) : (
          <div className="msg-row msg-row--assistant" key={message.id}>
            <div className="msg-main">
              <div className="msg-bubble msg-bubble--assistant">
                <MessageContent text={text} isStreaming={isLastMsg && isActivelyStreaming} artifacts={message.artifacts} />
              </div>
              <AIActionBar text={text} time={formattedTime} />
            </div>
          </div>
        );
      })}

      {/* 📍 协议驱动工具运行状态动画条 */}
      {toolStatus && (
        <div className="msg-row msg-row--assistant" key="active-tool-status">
          <div className="msg-main">
            <div className="msg-bubble msg-bubble--assistant msg-blockquote--tool-status">
              <span className="msg-tool-spinner" style={{ marginRight: "8px", display: "inline-flex", alignItems: "center" }}>
                <svg className="animate-spin-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" />
                </svg>
              </span>
              <span>{toolStatus.title}</span>
            </div>
          </div>
        </div>
      )}

      {/* 📍 极简无框 3 点加载动画 */}
      {isPendingAI && !toolStatus && (
        <div className="msg-row msg-row--assistant" key="pending-ai-loader">
          <div className="msg-main">
            <div className="msg-bubble msg-bubble--assistant">
              <div className="minimal-pending-loader">
                <span className="minimal-pending-dot"></span>
                <span className="minimal-pending-dot"></span>
                <span className="minimal-pending-dot"></span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
};

export default Chatbox;
